"use client";

import * as React from "react";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "Do you accept insurance?",
  answer: "We operate as an out-of-network practice to ensure the highest level of personalized, unhurried care. While we do not bill insurance directly, we provide detailed superbills that you can submit to your insurance carrier for out-of-network reimbursement. We recommend checking with your provider regarding your specific out-of-network benefits."
}, {
  question: "Which states do you serve remotely?",
  answer: "We are licensed to provide remote psychiatric consultations and medication management for patients located in New York, New Jersey, Connecticut, Florida, Illinois, Tennessee, Vermont, and Washington State. For patients in the New York City area, we also offer in-person appointments at our main office."
}, {
  question: "What is genetic evaluation and how does it help?",
  answer: "Genetic evaluation involves a simple non-invasive test that analyzes how your specific genes affect your response to various medications. This allows us to bypass the traditional 'trial and error' approach and prescribe medications that are scientifically more likely to be effective and well-tolerated by your body."
}, {
  question: "What ages of patients do you treat?",
  answer: "Our practice serves patients of all ages. Dr. Sharmat specializes in complex adult cases, while our board-certified nurse practitioner, Maryellen Stephens, is experienced in treating children, adolescents, and adults."
}, {
  question: "How do I schedule an appointment?",
  answer: "Scheduling is simple. You can reach our office by phone at (212) 804-7665 or via our contact form. Our administrative team will guide you through the intake process, answer any logistical questions, and help match you with the right clinician for your needs."
}, {
  question: "Where is your office located?",
  answer: "Our physical office is located at 315 5th Ave Rm 701, New York City, NY 10016. It is designed to be a discreet, comfortable, and welcoming space for all our patients."
}];
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="common-questions" ref={ref} className="relative bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-accent/20 blur-3xl opacity-40" />
        <div className="absolute top-[40%] right-[0%] w-[40%] h-[60%] rounded-full bg-primary/5 blur-3xl opacity-30" />
      </div>

      <div className="container relative mx-auto px-6 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24">
          {}
          <div className={`lg:col-span-5 flex flex-col justify-between transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6 tracking-tight">
                Common Questions
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Everything you need to know about our practice logistics, from
                insurance policies to remote care availability. We believe in
                transparency and clarity at every step of your treatment
                journey.
              </p>

              <div className="hidden lg:block space-y-6">
                <div className="p-6 rounded-2xl bg-secondary/30 border border-border/50 backdrop-blur-sm">
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Still have questions?
                  </h3>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Our team is available Monday–Tuesday, 11am-7pm, Wednesday-Thursday, 9am–4:30pm, and Friday 9am-1:30pm to assist you.
                  </p>
                  <div className="space-y-3">
                    <Link to="tel:+12128047665" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group">
                      <div className="flex items-center justify-center size-10 rounded-full bg-background border border-border group-hover:border-primary/30 transition-colors">
                        <Phone className="size-4 text-primary" />
                      </div>
                      <span className="font-medium">(212) 804-7665</span>
                    </Link>
                  </div>
                  <div className="mt-8">
                    <Button asChild className="w-full">
                      <Link to="/ContactPage">
                        Contact Us <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-b border-border/60 last:border-0 mb-2" data-index={i}>
                  <AccordionTrigger className="text-lg font-medium text-foreground hover:text-primary hover:no-underline py-6 text-left [&[data-state=open]]:text-primary transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {}
            <div className="mt-12 lg:hidden">
              <div className="p-6 rounded-2xl bg-secondary/30 border border-border/50 backdrop-blur-sm">
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Still have questions?
                </h3>
                <p className="text-muted-foreground mb-6 text-sm">
                  Reach out to our team directly.
                </p>
                <Button asChild className="w-full">
                  <Link to="/ContactPage">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}